import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { ArrowLeft, Loader2, Download, CheckCircle2, Send, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export default function FinalizeInvoice() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [form, setForm] = useState({
    from_company: { name: 'Millennials Maintenance', address: '', phone: '', email: '' },
    bill_to: { name: '', address: '', email: '' },
    invoice_date: new Date().toLocaleDateString('en-US'),
    due_days: 15,
    terms: 'Net 15',
    notes: 'Thank you for your business. Payment is due upon receipt.',
  });

  useEffect(() => { load(); }, [runId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/invoice-transformer/transform-runs/${runId}`);
      setRun(res.data);
      // Pre-fill bill_to from customer
      const siRes = await api.get(`/invoice-transformer/supplier-invoices/${res.data.supplier_invoice_id}`);
      setForm(p => ({
        ...p,
        bill_to: {
          name: siRes.data.customer_name || '',
          address: '',
          email: siRes.data.customer_email || '',
        }
      }));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/invoice-transformer/customer-invoices', {
        transform_run_id: runId,
        ...form,
      });
      setGenerated(res.data);
      toast.success('Invoice PDF generated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;
  if (!run) return null;

  const computed = run.computed || {};

  if (generated) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center animate-fade-in">
        <div className="w-20 h-20 rounded-sm bg-[#57EB31]/10 border-2 border-[#57EB31]/40 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-[#57EB31]" />
        </div>
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">// INVOICE READY</p>
        <h2 className="text-white font-['Manrope'] font-extrabold text-3xl mb-2">{generated.invoice_number}</h2>
        <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] mb-1">Total Due: <strong className="text-[#3B82F6]">{fmt(generated.totals?.total_due)}</strong></p>
        <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm mb-8">Sent to: {generated.bill_to?.name}</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <a href={`${BACKEND}${generated.pdf_url}?token=${localStorage.getItem('mm_token')}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 bg-[#57EB31] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#57EB31]/90 transition-colors"
            data-testid="download-generated-pdf">
            <Download size={16} /> Download PDF
          </a>
          <button onClick={async () => {
              setEmailing(true);
              try {
                const res = await api.post(`/invoice-transformer/customer-invoices/${generated.id}/send-email`, {});
                toast.success(`Invoice emailed to ${res.data.emailed_to}`);
              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
              finally { setEmailing(false); }
            }} disabled={emailing}
            className="flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors disabled:opacity-50"
            data-testid="email-invoice-btn">
            {emailing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
            {emailing ? 'Sending...' : 'Email to Customer'}
          </button>
          <button onClick={() => navigate('/ops/invoice-transformer')}
            className="px-6 py-3 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors">
            Back to Hub
          </button>
        </div>
        <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-xs mt-6">
          The customer can now download this invoice from their portal.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      <div className="flex items-center gap-4 pb-4 border-b border-[#2D3F5E]/60">
        <button onClick={() => navigate(-1)} className="text-[#5C6A7A] hover:text-[#3B82F6] transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-0.5">// STEP 3 — FINALIZE</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-xl">Final Invoice Editor</h1>
          <div className="flex items-center gap-4 mt-1">
            <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">Review, edit header fields, then generate the branded customer PDF</p>
            {run?.truck_unit_number && (
              <span className="text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 rounded-sm bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/30">
                Unit {run.truck_unit_number}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Form */}
        <div className="col-span-5 space-y-4">
          {/* From Company */}
          <div className="card-industrial p-5 space-y-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">From (Your Company)</p>
            {[['Company Name','name'],['Address','address'],['Phone','phone'],['Email','email']].map(([label,field]) => (
              <div key={field}>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">{label}</label>
                <input type="text" value={form.from_company[field]} onChange={e => setForm(p => ({...p, from_company: {...p.from_company, [field]: e.target.value}}))}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
            ))}
          </div>

          {/* Bill To */}
          <div className="card-industrial p-5 space-y-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A]">Bill To</p>
            {[['Client Name','name'],['Address','address'],['Email','email']].map(([label,field]) => (
              <div key={field}>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">{label}</label>
                <input type="text" value={form.bill_to[field]} onChange={e => setForm(p => ({...p, bill_to: {...p.bill_to, [field]: e.target.value}}))}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
            ))}
          </div>

          {/* Invoice meta */}
          <div className="card-industrial p-5 space-y-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A]">Invoice Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Invoice Date</label>
                <input type="text" value={form.invoice_date} onChange={e => setForm(p => ({...p, invoice_date: e.target.value}))}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Net Days</label>
                <input type="number" value={form.due_days} onChange={e => setForm(p => ({...p, due_days: e.target.value, terms: `Net ${e.target.value}`}))}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Notes / Payment Instructions</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={3}
                className="w-full px-3 py-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6] resize-none" />
            </div>
          </div>
        </div>

        {/* Right — Summary + Generate */}
        <div className="col-span-7 space-y-4">
          <div className="card-industrial p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A] mb-4">Invoice Summary</p>
            <div className="space-y-3">
              {(run.selected_items || []).filter(i => i.selected).map((item, idx) => {
                const finalUnit = item.override_final_unit_price ?? Math.round((item.unit_cost || 0) * (1 + run.markup_percent / 100) * 100) / 100;
                return (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-[#2D3F5E]/40">
                    <div>
                      <p className="text-sm text-white font-['IBM_Plex_Sans']">{item.desc_display || item.desc}</p>
                      <p className="text-xs text-[#5C6A7A] font-['JetBrains_Mono']">Qty: {item.qty}</p>
                    </div>
                    <p className="font-['JetBrains_Mono'] font-bold text-[#E8EAED]">{fmt(finalUnit * (item.qty || 1))}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-[#2D3F5E] space-y-2 text-sm font-['JetBrains_Mono']">
              <div className="flex justify-between text-[#B8C0CC]"><span>Subtotal</span><span>{fmt(computed.subtotal_after_markup)}</span></div>
              {computed.service_fee > 0 && <div className="flex justify-between text-[#B8C0CC]"><span>Service Fee</span><span>{fmt(computed.service_fee)}</span></div>}
              {computed.tax_amount > 0 && <div className="flex justify-between text-[#B8C0CC]"><span>Tax</span><span>{fmt(computed.tax_amount)}</span></div>}
              <div className="flex justify-between font-bold text-white text-base pt-1 border-t border-[#2D3F5E]">
                <span>TOTAL DUE</span><span className="text-[#3B82F6]">{fmt(computed.total_due)}</span>
              </div>
            </div>
          </div>

          <div className="card-industrial p-5 bg-[#57EB31]/5 border-[#57EB31]/20">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#57EB31] mb-3">Ready to Generate</p>
            <ul className="space-y-2 text-xs font-['IBM_Plex_Sans'] text-[#B8C0CC] mb-5">
              <li className="flex items-center gap-2"><CheckCircle2 size={13} className="text-[#57EB31]" /> Branded PDF with Millennials Maintenance header</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={13} className="text-[#57EB31]" /> Customer sees final prices only — no base costs or markup</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={13} className="text-[#57EB31]" /> PDF automatically added to customer's invoice list</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={13} className="text-[#57EB31]" /> Customer can download from their portal immediately</li>
            </ul>
            <button onClick={handleGenerate} disabled={generating} data-testid="generate-pdf-btn"
              className="w-full h-12 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(59,130,246,0.25)] flex items-center justify-center gap-2">
              {generating ? <><Loader2 size={16} className="animate-spin" /> Generating PDF...</> : <><Download size={16} /> Generate Customer Invoice PDF</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
